import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatStrictDistance } from "@/lib/relative-time"
import type { Locale } from "@/types"
import type { ShipmentParcel, ShipmentRow } from "@/types/shipments"

import { ConfirmAddressDialog } from "./confirm-address-dialog"
import { ShipmentAddressBlock } from "./shipment-address-block"
import { ShipmentRowContent, creatorLabelFor } from "./shipment-row"

/**
 * OKS-991 — one parcel in the Ready section (UX Spec §2).
 *
 * Everything going to one creator at one address: the header says who and how
 * many, the items say what, ONE revealed block says where and how to reach
 * them, and one CTA ships the box.
 *
 * # Anatomy, and why it is in this order
 *
 * header → items → contact → action, matching the operator's own sequence:
 * confirm who it is, pack the items, print the label, mark it gone (UX §6).
 * The revealed block sits BELOW the items because it is what you do last.
 *
 * # ⚠️ The card renders even for ONE item
 *
 * One anatomy to learn, not two (UX OQ-3). The only difference is that a
 * single-item card omits « Expédier seul » — the group CTA already is it, and
 * offering both would ask the operator to choose between two identical
 * actions.
 *
 * # ⚠️ No address, email or phone in the HEADER
 *
 * The split badge says how many addresses differ; it never says what they are.
 * That distinction is the reason the badge is allowed to render before the
 * reveal at all (PRD OQ-7): a count is not content.
 */
interface ShipmentParcelCardProps {
  parcel: ShipmentParcel
  locale: Locale
  /** The group CTA — the mark-shipped dialog in its parcel shape. */
  action?: React.ReactNode
  /** Per-item « Expédier seul », omitted on a single-item card. */
  renderItemAction?: (row: ShipmentRow) => React.ReactNode
}

/**
 * The id of a parcel card's header, used as its accessible name.
 *
 * ⚠️ EXPORTED so the `<li>` in `Brand/Shipments/Index` and the header rendered
 * here derive it from ONE place. `aria-labelledby` pointing at an id nobody
 * renders is silently inert — the attribute is present, the name resolves to
 * nothing, and a screen reader announces the bare list item. Two hand-written
 * template strings in two files is exactly how that drifts.
 */
export function parcelHeaderId(key: string): string {
  return `shipments-parcel-${key}-header`
}

export function ShipmentParcelCard({
  parcel,
  locale,
  action,
  renderItemAction,
}: ShipmentParcelCardProps) {
  const { t } = useTranslation()
  const label = creatorLabelFor(parcel.items[0])
  // OKS-1037 — confirm wins over edit: one unconfirmed item makes the parcel
  // "not confirmed", and the dialog then confirms every actionable item.
  const addressTargets = parcel.items.flatMap((item) =>
    item.source === "collab" && item.brand_address_action
      ? [{ id: item.id, snapshotVersion: item.snapshot_version ?? "" }]
      : [],
  )
  const addressAction = parcel.items.some(
    (item) =>
      item.source === "collab" && item.brand_address_action === "confirm",
  )
    ? "confirm"
    : "edit"
  const isMultiItem = parcel.items.length > 1

  return (
    <div
      className="flex flex-col gap-3"
      data-testid={`shipments-parcel-${parcel.key}`}
    >
      {/* UX §6 — this header IS the card's accessible name, so a screen
          reader announces "Marie — 3 articles dans ce colis" before the items
          rather than an unnamed list item among eleven identical ones. */}
      <div
        id={parcelHeaderId(parcel.key)}
        className="flex flex-wrap items-center gap-2"
      >
        <Avatar className="size-8">
          <AvatarFallback>{initialsFor(parcel.creator.name)}</AvatarFallback>
        </Avatar>
        <span className="font-medium break-words">{label}</span>

        {!parcel.creator.active && (
          <Badge variant="outline" data-testid="shipments-parcel-former-crew">
            {t("shipments.row.former_crew")}
          </Badge>
        )}

        {/* ⚠️ `items.length`, never a server-sent count. A second number for
            the same fact could disagree with the list beside it, and nothing
            would detect that a parcel's list is never paginated. */}
        <span
          className="text-muted-foreground text-xs"
          data-testid="shipments-parcel-item-count"
        >
          {t("shipments.parcel.items_in_parcel", {
            count: parcel.items.length,
          })}
        </span>

        {parcel.oldest_waited_at && (
          <Tooltip>
            {/* ⚠️ Base UI `render`, NOT Radix `asChild`. */}
            <TooltipTrigger
              render={
                <span
                  className="text-muted-foreground text-xs"
                  data-testid="shipments-parcel-age"
                />
              }
            >
              {formatStrictDistance(parcel.oldest_waited_at, locale)}
            </TooltipTrigger>
            <TooltipContent>
              {new Date(parcel.oldest_waited_at).toLocaleString(
                locale === "fr" ? "fr-FR" : "en-US",
              )}
            </TooltipContent>
          </Tooltip>
        )}

        {/* ⚠️ A WORD, never colour alone (UX §6). */}
        {parcel.address_variants && (
          <Badge variant="secondary" data-testid="shipments-parcel-split-badge">
            {t("shipments.parcel.split_badge")}
          </Badge>
        )}
      </div>

      {parcel.address_variants && (
        <p
          className="text-muted-foreground text-sm"
          data-testid="shipments-parcel-split-explanation"
        >
          {t("shipments.parcel.split_explanation", {
            count: parcel.address_variants,
          })}
        </p>
      )}

      {/* A nested list, so a screen reader announces the parcel's own label
          before its contents (UX §6). */}
      <ul className="flex flex-col gap-3">
        {parcel.items.map((row) => (
          <li
            key={`${row.source}-${row.id}`}
            className="border-muted flex flex-col gap-2 border-l-2 pl-3"
          >
            {/* ⚠️ `variant="parcel-item"`: the card states the creator and the
                wait once, and owns the destination. Without it each item would
                repeat the avatar AND render "no address on file" for a box
                that has a confirmed one. */}
            {/* ⚠️ « Expédier seul » goes through the row's OWN `action`
                slot rather than beside it, so the trigger lives INSIDE the
                row element. Every locator that scopes to a row — the Vitest
                dispatch arms, LQE's Playwright arms — finds it there; a
                sibling button is invisible to `row.querySelector` and the
                arms go quietly untested rather than red. */}
            <ShipmentRowContent
              row={row}
              section="ready"
              locale={locale}
              variant="parcel-item"
              action={isMultiItem ? renderItemAction?.(row) : undefined}
            />
          </li>
        ))}
      </ul>

      {/* ⚠️ Branch on PRESENCE, never truthiness: both keys are ABSENT outside
          the reveal window, and a truthiness check would also hide a block
          that is legitimately present. */}
      {"shipping_address" in parcel && parcel.shipping_address && (
        <ShipmentAddressBlock
          address={parcel.shipping_address}
          creatorLabel={label}
          locale={locale}
          contact={parcel.contact}
          testIdPrefix="shipments-parcel"
        />
      )}

      {/* OKS-1037 — « Confirmer l'adresse » / « Modifier l'adresse » under the
          address block, BEFORE « Marquer expédié », which stays primary (UX
          §2.1). It writes only the items the server marked actionable: a
          `creator` item and a gear item are never posted (PRD OQ6). */}
      {(addressTargets.length > 0 || action) && (
        <div className="flex flex-wrap justify-end gap-2">
          {addressTargets.length > 0 && (
            <ConfirmAddressDialog
              targets={addressTargets}
              action={addressAction}
              creatorLabel={label}
              address={parcel.shipping_address}
              contact={parcel.contact}
              locale={locale}
              triggerVariant={addressAction === "confirm" ? "outline" : "ghost"}
              testIdPrefix="shipments-parcel"
            />
          )}
          {action}
        </div>
      )}
    </div>
  )
}

function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
}
